import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../admin/ui';
import { Brand } from '../admin/Brand';
import { useAppearance } from '../../hooks/useAppearance';
import { Input } from '@avala/ui/components/primitives/input';
import { Label } from '@avala/ui/components/primitives/label';
import { useAuth } from '../../hooks/useAuth';
import { EmailLoginUnavailableError } from '../../lib/staff-login';

export function LoginPage() {
  useAppearance();
  const { user, loading, error, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) navigate('/', { replace: true });
  }, [user, loading, navigate]);

  async function startLogin(workEmail?: string): Promise<void> {
    if (submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await signIn(workEmail);
    } catch (cause) {
      setSubmitError(
        cause instanceof EmailLoginUnavailableError
          ? cause.message
          : 'Unable to start sign-in. Please try again or contact your administrator.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void startLogin(email);
  }

  return (
    <main className="grid min-h-dvh lg:grid-cols-2">
      <section className="flex flex-col justify-between border-b border-border bg-sidebar p-8 lg:border-b-0 lg:border-r lg:p-16">
        <Brand />
        <div className="max-w-lg py-12 lg:py-24">
          <p className="support-label mb-6">Avala Support</p>
          <h1 className="text-4xl font-normal tracking-tight sm:text-5xl">
            Every conversation.
            <br />
            One workspace.
          </h1>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-muted-foreground">
            The place for your team to help customers move forward.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">For Avala support staff.</p>
      </section>
      <section className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-normal tracking-tight">Sign in to Support</h2>
          <p className="mb-8 mt-3 text-sm leading-relaxed text-muted-foreground">
            Use your work email to access your workspace.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {(submitError || error) && (
              <p role="alert" className="text-sm text-error-text">
                {submitError || error}
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="work-email">Work email</Label>
              <Input
                id="work-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@avala.ai"
                required
                disabled={loading || submitting}
              />
            </div>
            <Button type="submit" variant="primary" className="w-full" disabled={loading || submitting}>
              {submitting ? 'Opening sign-in…' : 'Continue with email'}
            </Button>
            <p className="text-sm text-muted-foreground">
              You’ll verify your email on our secure sign-in page. No phone signup is needed.
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={loading || submitting}
              onClick={() => void startLogin()}
            >
              Continue with Google
            </Button>
          </form>
          <p className="mt-8 border-t border-border pt-6 text-xs leading-relaxed text-muted-foreground">
            Looking for help? Contact support from Mission Control or Arcade.
          </p>
        </div>
      </section>
    </main>
  );
}
